import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query,
  Timestamp,
  updateDoc,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import {
  CoffeePartner,
  CoffeeStockTransaction,
  CoffeeStockTransactionType,
  CoffeeType,
} from '../types/coffeeInventory';

const PARTNER_COLLECTION = 'coffee-partners';
const COFFEE_TYPE_COLLECTION = 'coffee-types';
const TRANSACTION_COLLECTION = 'coffee-stock-transactions';

const toDateValue = (value: any): Date => {
  if (!value) return new Date();
  if (value instanceof Date) return value;
  if (value?.toDate) return value.toDate();
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
};

const normalizeNumber = (value: number | string | undefined): number => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 0;
  return parsed;
};

const normalizePartnerContractItems = (items: any): CoffeePartner['contractItems'] => {
  if (!Array.isArray(items)) return [];
  const dedupedMap = new Map<string, { coffeeTypeId: string; coffeeTypeName: string; unitPrice: number }>();

  items.forEach((item) => {
    const coffeeTypeId = String(item?.coffeeTypeId || '').trim().toLowerCase();
    const coffeeTypeName = String(item?.coffeeTypeName || '').trim();
    const unitPrice = normalizeNumber(item?.unitPrice);
    if (!coffeeTypeId || !coffeeTypeName || unitPrice <= 0) return;
    dedupedMap.set(coffeeTypeId, {
      coffeeTypeId,
      coffeeTypeName,
      unitPrice,
    });
  });

  return Array.from(dedupedMap.values());
};

const normalizePartner = (raw: any): CoffeePartner => ({
  id: raw.id,
  name: String(raw.name || '').trim(),
  defaultDiscountRate: normalizeNumber(raw.defaultDiscountRate),
  contractItems: normalizePartnerContractItems(raw.contractItems),
  isActive: raw.isActive !== false,
  notes: String(raw.notes || ''),
  createdAt: toDateValue(raw.createdAt),
  updatedAt: toDateValue(raw.updatedAt),
});

const normalizeCoffeeType = (raw: any): CoffeeType => ({
  id: raw.id,
  name: String(raw.name || '').trim(),
  unit: String(raw.unit || '').trim(),
  unitPrice: normalizeNumber(raw.unitPrice),
  isActive: raw.isActive !== false,
  notes: String(raw.notes || ''),
  createdAt: toDateValue(raw.createdAt),
  updatedAt: toDateValue(raw.updatedAt),
});

const normalizeTransaction = (raw: any): CoffeeStockTransaction => {
  const quantity = normalizeNumber(raw.quantity);
  const unitPrice = normalizeNumber(raw.unitPrice);
  const totalAmount = normalizeNumber(raw.totalAmount) || quantity * unitPrice;
  const discountRate = normalizeNumber(raw.discountRate);
  const discountedAmount =
    normalizeNumber(raw.discountedAmount) || totalAmount * (1 - Math.max(0, Math.min(discountRate, 100)) / 100);

  return {
    id: raw.id,
    date: toDateValue(raw.date),
    transactionType: raw.transactionType as CoffeeStockTransactionType,
    coffeeTypeId: String(raw.coffeeTypeId || ''),
    coffeeTypeName: String(raw.coffeeTypeName || ''),
    quantity,
    unitPrice,
    totalAmount,
    partnerId: raw.partnerId || '',
    partnerName: raw.partnerName || '',
    discountRate,
    discountedAmount,
    storeName: raw.storeName || '',
    notes: raw.notes || '',
    createdBy: raw.createdBy || '',
    createdAt: toDateValue(raw.createdAt),
    updatedAt: toDateValue(raw.updatedAt),
  };
};

export const coffeeInventoryService = {
  async getAllPartners(): Promise<CoffeePartner[]> {
    const q = query(collection(db, PARTNER_COLLECTION), orderBy('name', 'asc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map((item) => normalizePartner({ id: item.id, ...item.data() }));
  },

  async addPartner(data: Omit<CoffeePartner, 'id' | 'createdAt' | 'updatedAt'>): Promise<CoffeePartner> {
    const payload = {
      ...data,
      name: data.name.trim(),
      defaultDiscountRate: normalizeNumber(data.defaultDiscountRate),
      contractItems: normalizePartnerContractItems(data.contractItems),
      isActive: data.isActive !== false,
      notes: (data.notes || '').trim(),
      createdAt: Timestamp.fromDate(new Date()),
      updatedAt: Timestamp.fromDate(new Date()),
    };

    const docRef = await addDoc(collection(db, PARTNER_COLLECTION), payload);
    return normalizePartner({ id: docRef.id, ...payload });
  },

  async updatePartner(id: string, data: Partial<CoffeePartner>): Promise<void> {
    const payload: Record<string, any> = {
      ...data,
      updatedAt: Timestamp.fromDate(new Date()),
    };
    if (data.name !== undefined) payload.name = String(data.name || '').trim();
    if (data.defaultDiscountRate !== undefined) payload.defaultDiscountRate = normalizeNumber(data.defaultDiscountRate);
    if (data.contractItems !== undefined) payload.contractItems = normalizePartnerContractItems(data.contractItems);
    if (data.notes !== undefined) payload.notes = String(data.notes || '').trim();

    await updateDoc(doc(db, PARTNER_COLLECTION, id), payload);
  },

  async deletePartner(id: string): Promise<void> {
    await deleteDoc(doc(db, PARTNER_COLLECTION, id));
  },

  async getAllCoffeeTypes(): Promise<CoffeeType[]> {
    const q = query(collection(db, COFFEE_TYPE_COLLECTION), orderBy('name', 'asc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map((item) => normalizeCoffeeType({ id: item.id, ...item.data() }));
  },

  async addCoffeeType(data: Omit<CoffeeType, 'id' | 'createdAt' | 'updatedAt'>): Promise<CoffeeType> {
    const payload = {
      ...data,
      name: data.name.trim(),
      unit: data.unit.trim(),
      unitPrice: normalizeNumber(data.unitPrice),
      isActive: data.isActive !== false,
      notes: (data.notes || '').trim(),
      createdAt: Timestamp.fromDate(new Date()),
      updatedAt: Timestamp.fromDate(new Date()),
    };

    const docRef = await addDoc(collection(db, COFFEE_TYPE_COLLECTION), payload);
    return normalizeCoffeeType({ id: docRef.id, ...payload });
  },

  async updateCoffeeType(id: string, data: Partial<CoffeeType>): Promise<void> {
    const payload: Record<string, any> = {
      ...data,
      updatedAt: Timestamp.fromDate(new Date()),
    };

    if (data.name !== undefined) payload.name = String(data.name || '').trim();
    if (data.unit !== undefined) payload.unit = String(data.unit || '').trim();
    if (data.unitPrice !== undefined) payload.unitPrice = normalizeNumber(data.unitPrice);
    if (data.notes !== undefined) payload.notes = String(data.notes || '').trim();

    await updateDoc(doc(db, COFFEE_TYPE_COLLECTION, id), payload);
  },

  async deleteCoffeeType(id: string): Promise<void> {
    await deleteDoc(doc(db, COFFEE_TYPE_COLLECTION, id));
  },

  async getAllTransactions(): Promise<CoffeeStockTransaction[]> {
    const q = query(collection(db, TRANSACTION_COLLECTION), orderBy('date', 'desc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map((item) => normalizeTransaction({ id: item.id, ...item.data() }));
  },

  async addTransaction(
    data: Omit<CoffeeStockTransaction, 'id' | 'createdAt' | 'updatedAt' | 'totalAmount' | 'discountedAmount'>
  ): Promise<CoffeeStockTransaction> {
    const quantity = normalizeNumber(data.quantity);
    const unitPrice = normalizeNumber(data.unitPrice);
    const totalAmount = quantity * unitPrice;
    const discountRate = normalizeNumber(data.discountRate);
    const discountedAmount = totalAmount * (1 - Math.max(0, Math.min(discountRate, 100)) / 100);

    const payload = {
      ...data,
      date: Timestamp.fromDate(toDateValue(data.date)),
      quantity,
      unitPrice,
      totalAmount,
      discountRate,
      discountedAmount,
      partnerId: data.partnerId || '',
      partnerName: data.partnerName || '',
      storeName: data.storeName || '',
      notes: (data.notes || '').trim(),
      createdAt: Timestamp.fromDate(new Date()),
      updatedAt: Timestamp.fromDate(new Date()),
    };

    const docRef = await addDoc(collection(db, TRANSACTION_COLLECTION), payload);
    return normalizeTransaction({ id: docRef.id, ...payload });
  },

  async updateTransaction(
    id: string,
    data: Partial<Omit<CoffeeStockTransaction, 'id' | 'createdAt' | 'updatedAt' | 'totalAmount' | 'discountedAmount'>>
  ): Promise<void> {
    const quantity = normalizeNumber(data.quantity);
    const unitPrice = normalizeNumber(data.unitPrice);
    const discountRate = normalizeNumber(data.discountRate);

    const hasPriceInputs = data.quantity !== undefined || data.unitPrice !== undefined || data.discountRate !== undefined;
    const totalAmount = quantity * unitPrice;
    const discountedAmount = totalAmount * (1 - Math.max(0, Math.min(discountRate, 100)) / 100);

    const payload: Record<string, any> = {
      ...data,
      updatedAt: Timestamp.fromDate(new Date()),
    };

    if (data.date !== undefined) payload.date = Timestamp.fromDate(toDateValue(data.date));
    if (data.quantity !== undefined) payload.quantity = quantity;
    if (data.unitPrice !== undefined) payload.unitPrice = unitPrice;
    if (data.discountRate !== undefined) payload.discountRate = discountRate;
    if (data.notes !== undefined) payload.notes = String(data.notes || '').trim();
    if (data.storeName !== undefined) payload.storeName = String(data.storeName || '').trim();
    if (data.partnerName !== undefined) payload.partnerName = String(data.partnerName || '').trim();
    if (data.partnerId !== undefined) payload.partnerId = String(data.partnerId || '').trim();

    if (hasPriceInputs) {
      payload.totalAmount = totalAmount;
      payload.discountedAmount = discountedAmount;
    }

    await updateDoc(doc(db, TRANSACTION_COLLECTION, id), payload);
  },

  async deleteTransaction(id: string): Promise<void> {
    await deleteDoc(doc(db, TRANSACTION_COLLECTION, id));
  },
};
